import { useMemo, useState } from "react";
import { ApexOptions } from "apexcharts";
import { Activity } from "lucide-react";
import { getAdminTradovateQuotaHistory, useQuery } from "wasp/client/operations";
import { Card, CardContent } from "../../../client/components/ui/card";
import LazyApexChart from "../../../client/components/LazyApexChart";
import { chartColors } from "../../../dashboard/widgets/chart/chartColors";
import { useThemeId } from "../../../client/components/ui/card-section-header";
import useTranslation from "../../../client/hooks/useTranslation";

// 48 h par défaut : montre le cycle jour/nuit complet et les pics d'ouverture
// de marché sans écraser la courbe, ce que 24 h ne permet pas de comparer.
const DEFAULT_HOURS = 48;

type AllowedHours = 24 | 48 | 168 | 720;

const TradovateQuotaHistoryCard = () => {
  const t = useTranslation();
  // `chartColors()` lit les variables CSS du thème actif : sans cette dépendance,
  // les couleurs du graphe resteraient figées sur le thème du premier rendu.
  const themeId = useThemeId();
  const [hours, setHours] = useState<AllowedHours>(DEFAULT_HOURS);
  const { data } = useQuery(getAdminTradovateQuotaHistory, { hours });

  const ranges: { value: AllowedHours; label: string }[] = [
    { value: 24, label: t.adminDashboard.quotaHistoryRange24h },
    { value: 48, label: t.adminDashboard.quotaHistoryRange48h },
    { value: 168, label: t.adminDashboard.quotaHistoryRange7d },
    { value: 720, label: t.adminDashboard.quotaHistoryRange30d },
  ];

  const points = data?.points ?? [];
  const hasData = points.some((p) => p.calls > 0 || p.rateLimitHits > 0);

  const options = useMemo((): ApexOptions => {
    const c = chartColors();

    // Marqueurs discrets sur les seules heures ayant subi un rate limit. Le
    // rayon croît avec le nombre pour qu'une rafale se distingue d'un incident
    // isolé sans ajouter d'axe secondaire trompeur.
    const discreteMarkers = points
      .map((p, index) =>
        p.rateLimitHits > 0
          ? {
              seriesIndex: 0,
              dataPointIndex: index,
              fillColor: c.negative,
              strokeColor: c.negative,
              size: Math.min(4 + p.rateLimitHits, 9),
            }
          : null,
      )
      .filter((m): m is NonNullable<typeof m> => m !== null);

    return {
      chart: {
        type: "area",
        height: 300,
        fontFamily:
          "'Red Hat Text', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif",
        toolbar: { show: false },
        zoom: { enabled: false },
      },
      colors: [c.series[0]],
      dataLabels: { enabled: false },
      stroke: { width: 2, curve: "straight" },
      fill: {
        type: "gradient",
        gradient: { opacityFrom: 0.35, opacityTo: 0 },
      },
      markers: { size: 0, discrete: discreteMarkers },
      grid: {
        borderColor: c.grid,
        xaxis: { lines: { show: false } },
        yaxis: { lines: { show: true } },
      },
      xaxis: {
        categories: points.map((p) => p.hourStartedAt),
        type: "category",
        tickAmount: 8,
        axisBorder: { show: false },
        axisTicks: { show: false },
        labels: {
          style: { colors: c.axis },
          // Sur 7 j et 30 j, afficher l'heure de chaque point rendrait l'axe
          // illisible : on bascule sur le jour au-delà de 48 h.
          formatter: (value: string) => {
            const d = new Date(value);
            if (Number.isNaN(d.getTime())) return "";
            return hours > 48
              ? d.toLocaleDateString(undefined, {
                  day: "2-digit",
                  month: "2-digit",
                })
              : d.toLocaleTimeString(undefined, {
                  hour: "2-digit",
                  minute: "2-digit",
                });
          },
        },
      },
      yaxis: {
        min: 0,
        labels: { style: { colors: c.axis } },
      },
      // Repère du plafond : sans lui, la hauteur de la courbe ne dit rien de la
      // marge restante.
      annotations: {
        yaxis: [
          {
            y: data?.quota ?? 600,
            borderColor: c.negative,
            strokeDashArray: 4,
            label: {
              text: `${t.adminDashboard.quotaHistoryQuotaLine} ${data?.quota ?? 600}`,
              // Texte sur pastille rouge : on reprend le fond du graphe, qui
              // contraste avec le rouge dans les 4 thèmes.
              style: { background: c.negative, color: c.chartBg },
            },
          },
        ],
      },
      tooltip: {
        custom: ({ dataPointIndex }: { dataPointIndex: number }) => {
          const p = points[dataPointIndex];
          if (!p) return "";
          const when = new Date(p.hourStartedAt).toLocaleString();
          const top = p.topUsers
            .map(
              (u) =>
                `<div style="display:flex;gap:12px;justify-content:space-between">
                   <span>${u.userEmail}</span><span>${u.calls}</span>
                 </div>`,
            )
            .join("");
          const rl =
            p.rateLimitHits > 0
              ? `<div style="color:${c.negative}">${t.adminDashboard.quotaHistoryRateLimitLabel}: ${p.rateLimitHits}</div>`
              : "";
          return `<div style="padding:8px;font-size:12px">
                    <div style="font-weight:600">${when}</div>
                    <div>${t.adminDashboard.quotaHistoryCallsLabel}: ${p.calls}</div>
                    ${rl}
                    ${top}
                  </div>`;
        },
      },
      legend: { show: false },
    };
  }, [points, hours, data?.quota, t, themeId]);

  const series = useMemo(
    () => [
      {
        name: t.adminDashboard.quotaHistoryCallsLabel,
        data: points.map((p) => p.calls),
      },
    ],
    [points, t],
  );

  return (
    <div>
      <h2 className="text-foreground mb-1 flex items-center gap-2 text-lg font-semibold">
        <Activity className="h-4 w-4" />
        {t.adminDashboard.quotaHistoryTitle}
      </h2>
      <p className="text-muted-foreground mb-4 text-xs">
        {t.adminDashboard.quotaHistoryHint}
      </p>

      <Card>
        <CardContent className="pt-4">
          <div className="mb-4 flex items-center justify-between gap-4">
            <div className="flex items-center gap-4 text-sm">
              <span className="text-muted-foreground">
                {t.adminDashboard.quotaHistoryPeakLabel}{" "}
                <span className="text-foreground font-mono">
                  {data?.peakCalls ?? 0}
                </span>
              </span>
              <span className="text-muted-foreground">
                {t.adminDashboard.quotaHistoryRateLimitLabel}{" "}
                <span
                  className={
                    (data?.totalRateLimitHits ?? 0) > 0
                      ? "text-pnlNegative font-mono"
                      : "text-foreground font-mono"
                  }
                >
                  {data?.totalRateLimitHits ?? 0}
                </span>
              </span>
            </div>

            <div className="flex gap-1">
              {ranges.map((r) => (
                <button
                  key={r.value}
                  type="button"
                  onClick={() => setHours(r.value)}
                  className={`rounded px-2 py-1 text-xs transition-colors ${
                    hours === r.value
                      ? "bg-primary text-primary-foreground"
                      : "text-muted-foreground hover:bg-accent"
                  }`}
                >
                  {r.label}
                </button>
              ))}
            </div>
          </div>

          {/* État vide explicite : au déploiement la courbe EST vide, et sans ce
              message ça se lit comme une panne plutôt que comme un historique
              qui démarre. */}
          {!hasData ? (
            <p className="text-muted-foreground py-12 text-center text-sm">
              {t.adminDashboard.quotaHistoryEmpty}
            </p>
          ) : (
            <LazyApexChart
              options={options}
              series={series}
              type="area"
              height={300}
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default TradovateQuotaHistoryCard;
